import React, { useState, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useSelector, useDispatch } from 'react-redux'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import GenericSelect from 'components/form/GenericSelect'
import SearchButton from 'components/form/SearchButton'
import { ClientRoute } from 'routes/client'
import { clearGpsMappingStatus, selectCourse } from 'store/course/slice'
import { selectLocale } from 'store/locale/slice'
import { localizedDate } from 'utils/formatDate'
import { copyCourseGpsMapping, flipCourseGpsVersionMapping } from 'store/course/thunks'
import { MenuItem, Select } from '@mui/material'
import { parseInteger } from 'utils/numbers'

enum CourseHoles {
  Front9 = 'f9',
  Back9 = 'b9',
}
interface Props {
  course: Course
}

export default function CourseGpsLocations({ course }: Props): JSX.Element {
  const [selectedGpsVersion, setSelectedGpsVersion] = useState<number | null>(null)
  const [courseIdFront9, setCourseIdFront9] = useState<string | null>(null)
  const [courseIdBack9, setCourseIdBack9] = useState<string | null>(null)
  const [front9Holes, setFront9Holes] = useState<CourseHoles>(CourseHoles.Front9)
  const [back9Holes, setBack9Holes] = useState<CourseHoles>(CourseHoles.Back9)

  const intl = useIntl()
  const dispatch = useDispatch()
  const locale = useSelector(selectLocale)
  const { flipGpsVersionMappingStatus, copyCourseGpsMappingStatus, gpsVersionStatus } = useSelector(selectCourse)

  useEffect(() => {
    return () => {
      dispatch(clearGpsMappingStatus())
    }
  }, [dispatch])

  const getSortedVersions = useCallback(() => {
    const gpsPoints = course.gpsPoints || []
    const versions = [...new Map(gpsPoints.map((gps: CourseGps) => [gps.gpsVersion, gps])).values()]

    return versions.sort((a, b) => {
      const bVersion = b.gpsVersion || 0
      const aVersion = a.gpsVersion || 0
      return bVersion > aVersion ? 1 : -1
    })
  }, [course])

  useEffect(() => {
    if ([flipGpsVersionMappingStatus, copyCourseGpsMappingStatus, gpsVersionStatus].includes('succeeded')) {
      const latestVersion = getSortedVersions()[0]?.gpsVersion
      setSelectedGpsVersion(latestVersion)
    }
  }, [flipGpsVersionMappingStatus, copyCourseGpsMappingStatus, gpsVersionStatus, getSortedVersions])

  useEffect(() => {
    if (copyCourseGpsMappingStatus === 'succeeded') {
      setCourseIdFront9(null)
      setCourseIdBack9(null)
    }
  }, [copyCourseGpsMappingStatus])

  const getCreatedDate = (gps: CourseGps) => {
    const date = gps?.createdAt ? new Date(gps.createdAt.date) : null
    if (!date) {
      return '-'
    }
    return localizedDate(date, locale.language.code)
  }

  const getGpsVersionOptions = (): SelectOption[] => {
    return getSortedVersions().map((gps: CourseGps) => {
      const version = gps.gpsVersion || 0
      const date = getCreatedDate(gps)
      const officialText = intl.formatMessage({ id: 'strings.official' })
      const official = gps.isOfficial ? ` (${officialText})` : ''
      const label = `V${version} - ${date}${official}`
      return { key: version, label: label }
    })
  }

  const getDefaultVersion = () => {
    const versions = getSortedVersions()
    const official = versions.filter((gps: CourseGps) => gps.isOfficial)
    return official.length > 0 ? official[0].gpsVersion : versions[0]?.gpsVersion
  }

  const getSelectedVersion = () => {
    const version = selectedGpsVersion !== null ? selectedGpsVersion : getDefaultVersion()
    return typeof version === 'string' ? parseInt(version) : version
  }

  const syncSelectedVersion = (value: number | string) => {
    const newValue = typeof value === 'string' ? parseInt(value) : value
    setSelectedGpsVersion(newValue)
  }

  const flipVersionMapping = () => {
    const payload: CourseGpsVersionPayload = {
      courseId: course.id,
      successMessage: intl.formatMessage({ id: 'strings.newCourseGpsVersionCreated' }),
      data: { gpsVersion: getSelectedVersion() },
    }
    dispatch(flipCourseGpsVersionMapping(payload))
  }

  const copyGpsMapping = () => {
    if (!courseIdFront9 || !courseIdBack9) {
      return
    }

    const payload: CourseGpsMappingCopyPayload = {
      courseId: course.id,
      successMessage: intl.formatMessage({ id: 'strings.newCourseGpsVersionCreated' }),
      data: {
        courseIdFront9: parseInt(courseIdFront9),
        courseIdBack9: parseInt(courseIdBack9),
        originalCourseHolesForFront9: front9Holes,
        originalCourseHolesForBack9: back9Holes,
      },
    }

    dispatch(copyCourseGpsMapping(payload))
  }

  const getGpsEditorUrl = () => {
    const isDefault = getSelectedVersion() === getDefaultVersion()
    const params = !isDefault ? `courseId=${course.id}&version=${getSelectedVersion()}` : `courseId=${course.id}`
    return ClientRoute.ADMIN_COURSE_GPS_EDITOR(params)
  }

  const courseIdInputsValid = (): boolean => {
    const front9IsNumeric = courseIdFront9 ? !isNaN(parseInt(courseIdFront9)) : false
    const back9IsNumeric = courseIdBack9 ? !isNaN(parseInt(courseIdBack9)) : false

    return front9IsNumeric && back9IsNumeric
  }

  return (
    <>
      <GpsLocationsContainer>
        <TopContainer>
          <SectionTitle>{intl.formatMessage({ id: 'strings.gpsLocations' })}</SectionTitle>
          <Link to={getGpsEditorUrl()} target="_blank">
            <OpenGpsEditor>{intl.formatMessage({ id: 'strings.openGpsEditor' })}</OpenGpsEditor>
          </Link>
        </TopContainer>

        <SelectContainer>
          <span>{intl.formatMessage({ id: 'strings.selectedGpsVersion' })}</span>
          <SelectVersion
            name="selectedGpsVersion"
            options={getGpsVersionOptions()}
            initialValue={getSelectedVersion()}
            syncChange={syncSelectedVersion}
          />
        </SelectContainer>

        <MappingContainer>
          <MappingRow>
            {intl.formatMessage({ id: 'strings.flipFrontAndBack9' })}
            <ActionButton
              text={intl.formatMessage({ id: 'strings.execute' })}
              onClick={flipVersionMapping}
              status={flipGpsVersionMappingStatus}
            />
          </MappingRow>
          <Divider />
          <MappingRow>
            <CopyGpsMappingTitle>{intl.formatMessage({ id: 'strings.copyGpsMappingFrom' })}</CopyGpsMappingTitle>

            <InputContainer>
              <span>{intl.formatMessage({ id: 'strings.front9' })}</span>
              <GpsInput
                name="front9"
                type="text"
                placeholder={intl.formatMessage({ id: 'strings.courseId' })}
                onChange={(e) => setCourseIdFront9(parseInteger(e.target.value))}
                value={courseIdFront9 || ''}
              />
              <GpsHolesSelect
                label={intl.formatMessage({ id: 'strings.holes' })}
                variant="outlined"
                fullWidth
                value={front9Holes}
                onChange={(e) => setFront9Holes(e.target.value as CourseHoles)}
              >
                <MenuItem value={CourseHoles.Front9}>{intl.formatMessage({ id: 'strings.front9' })}</MenuItem>
                <MenuItem value={CourseHoles.Back9}>{intl.formatMessage({ id: 'strings.back9' })}</MenuItem>
              </GpsHolesSelect>
            </InputContainer>
            <InputContainer>
              <span>{intl.formatMessage({ id: 'strings.back9' })}</span>
              <GpsInput
                name="back9"
                type="text"
                placeholder={intl.formatMessage({ id: 'strings.courseId' })}
                onChange={(e) => setCourseIdBack9(parseInteger(e.target.value))}
                value={courseIdBack9 || ''}
              />
              <GpsHolesSelect
                label={intl.formatMessage({ id: 'strings.holes' })}
                variant="outlined"
                fullWidth
                value={back9Holes}
                onChange={(e) => setBack9Holes(e.target.value as CourseHoles)}
              >
                <MenuItem value={CourseHoles.Front9}>{intl.formatMessage({ id: 'strings.front9' })}</MenuItem>
                <MenuItem value={CourseHoles.Back9}>{intl.formatMessage({ id: 'strings.back9' })}</MenuItem>
              </GpsHolesSelect>
            </InputContainer>

            <ActionButton
              status={copyCourseGpsMappingStatus}
              text={intl.formatMessage({ id: 'strings.execute' })}
              onClick={copyGpsMapping}
              disabled={!courseIdInputsValid()}
            />
          </MappingRow>
        </MappingContainer>
      </GpsLocationsContainer>
    </>
  )
}

const GpsLocationsContainer = styled.div`
  padding: 0 20px;

  & tbody tr {
    &:nth-child(even) {
      background-color: ${(props) => props.theme.customPalette.lightGray};
    }
  }
`

const TopContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 30px;
  margin-bottom: 25px;
`

const SectionTitle = styled.h2`
  font-style: italic;
  font-weight: 800;
  font-size: 20px;
  line-height: 27px;
  color: ${(props) => props.theme.customPalette.darkGray2};
`

const SelectContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 30px;
`

const SelectVersion = styled(GenericSelect)`
  background-color: ${(props) => props.theme.customPalette.lightGray};
  max-width: 300px;
  margin: 0;
`

const MappingContainer = styled.div`
  max-width: 600px;
  margin: 20px;
  padding: 20px;
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.darkGray};
  border-radius: 5px;
`

const MappingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 20px;
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: -25px;

  & > span {
    margin-bottom: 5px;
  }
`

const GpsInput = styled.input`
  background-color: ${(props) => props.theme.customPalette.light};
  border: 1px solid;
  border-color: ${(props) => props.theme.customPalette.lightGray2};
  border-radius: 4px;
  height: 40px;
  padding: 0 0 0 10px;
  font-size: 16px;
  line-height: 24px;
  color: #404040;
  width: calc(100% - 10px);
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

const ActionButton = styled(SearchButton)``

const OpenGpsEditor = styled.div`
  background-color: ${(props) => props.theme.customPalette.light};
  color: ${(props) => props.theme.palette.primary.main};
  border: 2px solid;
  border-color: ${(props) => props.theme.palette.primary.main};
  border-radius: 6px;
  font-weight: 700;
  padding: 10px 20px;
`

const Divider = styled.div`
  width: 100%;
  height: 1px;
  margin: 10px 0 40px;
  background-color: ${(props) => props.theme.customPalette.lightGray};
`

const GpsHolesSelect = styled(Select)`
  margin-top: 10px;
  & fieldset > legend {
    visibility: initial;

    & > span {
      background-color: ${(props) => props.theme.customPalette.light};
    }
  }
`

const CopyGpsMappingTitle = styled.div``
